// src/components/tools/room-ideas/RoomIdeaForm.jsx

import React from "react";
import {
  ROOM_TYPES,
  STYLE_OPTIONS,
  BUDGET_RANGES
} from "./constants";

/**
 * Resize + compress image before sending to API
 * Keeps payload small and fast
 */
const optimizeImage = (file, maxSize = 1024, quality = 0.85) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target.result;
    };

    img.onload = () => {
      const canvas = document.createElement("canvas");
      let { width, height } = img;

      if (width > height && width > maxSize) {
        height = Math.round((height * maxSize) / width);
        width = maxSize;
      } else if (height > maxSize) {
        width = Math.round((width * maxSize) / height);
        height = maxSize;
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);

      const dataUrl = canvas.toDataURL("image/jpeg", quality);
      resolve(dataUrl);
    };

    img.onerror = reject;
    reader.onerror = reject;

    reader.readAsDataURL(file);
  });
};

const RoomIdeaForm = ({
  values,
  errors,
  isSubmitting,
  apiError,
  onChange,
  onSubmit
}) => {

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    onChange(name, value);
  };

  const handleColorChange = (name) => (e) => {
    onChange(name, e.target.value);
  };

  // -----------------------------
  // Image Upload (NO API CALL)
  // -----------------------------
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const optimizedBase64 = await optimizeImage(file);

      onChange("referenceImage", optimizedBase64);
    } catch (err) {
      console.error("Image processing failed", err);
    }
  };

  return (
    <div className="room-idea-form">
      <h2>Imagine Your Room Upgrade</h2>

      {/* Room */}
      <div className="form-group">
        <label>Which room do you want to explore?</label>
        <select
          name="roomType"
          value={values.roomType}
          onChange={handleInputChange}
          disabled={isSubmitting}
        >
          <option value="">Select a room</option>
          {ROOM_TYPES.map(room => (
            <option key={room.value} value={room.value}>
              {room.label}
            </option>
          ))}
        </select>
        {errors.roomType && <div className="error">{errors.roomType}</div>}
      </div>

      {/* Style */}
      <div className="form-group">
        <label>Style</label>
        <select
          name="style"
          value={values.style}
          onChange={handleInputChange}
          disabled={isSubmitting}
        >
          <option value="">Select a style</option>
          {STYLE_OPTIONS.map(style => (
            <option key={style.value} value={style.value}>
              {style.label}
            </option>
          ))}
        </select>
        {errors.style && <div className="error">{errors.style}</div>}
      </div>

      {/* Budget */}
      <div className="form-group">
        <label>Budget</label>
        <select
          name="budget"
          value={values.budget}
          onChange={handleInputChange}
          disabled={isSubmitting}
        >
          <option value="">Select a budget</option>
          {BUDGET_RANGES.map(b => (
            <option key={b.value} value={b.value}>
              {b.label}
            </option>
          ))}
        </select>
        {errors.budget && <div className="error">{errors.budget}</div>}
      </div>

      {/* Colors */}
      <div className="form-group">
        <label>Primary color</label>
        <input
          type="color"
          value={values.primaryColor || "#ffffff"}
          onChange={handleColorChange("primaryColor")}
          disabled={isSubmitting}
        />
        {errors.primaryColor && (
          <div className="error">{errors.primaryColor}</div>
        )}
      </div>

      <div className="form-group">
        <label>Secondary color</label>
        <input
          type="color"
          value={values.secondaryColor || "#000000"}
          onChange={handleColorChange("secondaryColor")}
          disabled={isSubmitting}
        />
        {errors.secondaryColor && (
          <div className="error">{errors.secondaryColor}</div>
        )}
      </div>

      {/* Upload */}
      <div className="form-group">
        <label>Upload a photo of your room (optional)</label>
        <input
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          disabled={isSubmitting}
        />

        {values.referenceImage && (
          <img
            src={values.referenceImage}
            alt="Uploaded room preview"
            style={{
              maxWidth: 240,
              marginTop: 12,
              borderRadius: 6,
              border: "1px solid #ddd"
            }}
          />
        )}
      </div>

      {apiError && <div className="error">{apiError}</div>}

      <button onClick={onSubmit} disabled={isSubmitting}>
        {isSubmitting ? "Generating Ideas…" : "Explore Ideas"}
      </button>
    </div>
  );
};

export default RoomIdeaForm;
